import { useQuery, useQueryClient } from '@tanstack/react-query'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useServerRequests } from '~/hooks/useRequests'
import { getStaticTokenLogo } from '~/hooks/useTokenMetadata/logoUtils'
import { getVaultAsset } from '~/utils/vault'

import type { IAddress } from '~/types'

// Vault shares have no logo of their own; look up the underlying asset's.
const getLookupToken = (token: {
  address: IAddress
  chainId: SupportedChainId
}) => {
  const asset = getVaultAsset(token.chainId, token.address)
  return asset ? { chainId: token.chainId, address: asset } : token
}

/**
 * Hook that takes a list of tokens and returns their logo URIs.
 * Uses batched requests to efficiently fetch multiple logos.
 *
 * @param tokens - Array of tokens with address and chainId
 * @returns Array of logo URIs (or undefined) in the same order as input tokens
 */
export const useTokenLogos = (
  tokens: { address: IAddress; chainId: SupportedChainId }[],
) => {
  const { serverPostRequest } = useServerRequests()
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [QUERY_KEYS.TOKEN_LOGOS, tokens],
    queryFn: async () => {
      const tokensWithLogos = new Map<string, string>()
      const tokenLookupEntries = tokens.map((token) => ({
        requestedToken: token,
        lookupToken: getLookupToken(token),
      }))

      // Check for static logos and cached logos
      tokenLookupEntries.forEach(({ requestedToken, lookupToken }) => {
        const staticLogo = getStaticTokenLogo(
          lookupToken.address,
          lookupToken.chainId,
        )
        if (staticLogo) {
          tokensWithLogos.set(
            `${requestedToken.chainId}:${requestedToken.address}`,
            staticLogo,
          )
        } else {
          const cached: string | undefined = queryClient.getQueryData([
            QUERY_KEYS.TOKEN_LOGO,
            lookupToken.address,
            lookupToken.chainId,
          ])
          if (cached) {
            tokensWithLogos.set(
              `${requestedToken.chainId}:${requestedToken.address}`,
              cached,
            )
          }
        }
      })

      // Fetch remaining logos from server
      const tokensMissingLogos = tokenLookupEntries.filter(
        ({ requestedToken }) =>
          !tokensWithLogos.has(
            `${requestedToken.chainId}:${requestedToken.address}`,
          ),
      )
      if (tokensMissingLogos.length > 0) {
        const result = await serverPostRequest<(string | undefined)[]>({
          path: '/api/public/token/metadata/logos',
          params: tokensMissingLogos.map(({ lookupToken }) => lookupToken),
          // This is actually a fetch, it uses a POST to support large list of tokens, so we don't need an error message
          errorMessage: '',
        })

        // Set new logos in cache
        if (result) {
          tokensMissingLogos.forEach(
            ({ requestedToken, lookupToken }, index) => {
              const logo = result.data[index]
              if (logo) {
                queryClient.setQueryData(
                  [
                    QUERY_KEYS.TOKEN_LOGO,
                    lookupToken.address,
                    lookupToken.chainId,
                  ],
                  logo,
                )
                tokensWithLogos.set(
                  `${requestedToken.chainId}:${requestedToken.address}`,
                  logo,
                )
              }
            },
          )
        }
      }

      // Return the logos in the same order as the tokens
      return tokens.map((token) =>
        tokensWithLogos.get(`${token.chainId}:${token.address}`),
      )
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: tokens.length > 0,
  })
}
